<script setup lang="ts">
import {ref, computed} from 'vue'
import {ChevronsUpDown, LogOut, Settings, Brain, Bell} from 'lucide-vue-next'
import {SidebarMenu, SidebarMenuItem, SidebarMenuButton, useSidebar} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import SettingsDialog from '@/components/SettingsDialog.vue'
import MemoriesDialog from '@/components/MemoriesDialog.vue'
import AlarmsDialog from '@/components/AlarmsDialog.vue'
import {useTelegramStore} from '@/stores/telegram'

const {isMobile} = useSidebar()
const telegramStore = useTelegramStore()

const showLogoutDialog = ref(false)
const showSettingsDialog = ref(false)
const showMemoriesDialog = ref(false)
const showAlarmsDialog = ref(false)
const photoFailed = ref(false)

const user = computed(() => telegramStore.initDataUnsafe?.user)

const showPhoto = computed(() => user.value?.photo_url && !photoFailed.value)

function onPhotoError() {
  photoFailed.value = true
}

function onPhotoLoad(e: Event) {
  const img = e.target as HTMLImageElement
  if (img.naturalWidth <= 1 || img.naturalHeight <= 1) {
    photoFailed.value = true
  }
}

const displayName = computed(() => {
  if (!user.value) return ''
  return [user.value.first_name, user.value.last_name].filter(Boolean).join(' ')
})

const initials = computed(() => {
  if (!user.value) return '?'
  const parts = [user.value.first_name, user.value.last_name].filter(Boolean)
  return parts.map((p) => p![0]).join('')
})

function handleLogout() {
  telegramStore.logout()
  window.location.reload()
}
</script>

<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
            <img
              v-if="showPhoto"
              :src="user!.photo_url"
              :alt="displayName"
              class="h-8 w-8 shrink-0 rounded-lg object-cover"
              @error="onPhotoError"
              @load="onPhotoLoad" />
            <div
              v-else
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
              {{ initials }}
            </div>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">{{ displayName }}</span>
              <span v-if="user?.username" class="truncate text-xs text-muted-foreground"> @{{ user.username }} </span>
            </div>
            <ChevronsUpDown class="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          class="w-[var(--reka-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
          :side="isMobile ? 'bottom' : 'top'"
          align="end"
          :side-offset="4">
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <img
                v-if="showPhoto"
                :src="user!.photo_url"
                :alt="displayName"
                class="h-8 w-8 rounded-lg object-cover"
                @error="onPhotoError"
                @load="onPhotoLoad" />
              <div
                v-else
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
                {{ initials }}
              </div>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-medium">{{ displayName }}</span>
                <span v-if="user?.username" class="truncate text-xs text-muted-foreground"> @{{ user.username }} </span>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem @select="showSettingsDialog = true">
              <Settings class="mr-2 size-4" />
              Настройки
            </DropdownMenuItem>
            <DropdownMenuItem @select="showMemoriesDialog = true">
              <Brain class="mr-2 size-4" />
              Память Дживса
            </DropdownMenuItem>
            <DropdownMenuItem @select="showAlarmsDialog = true">
              <Bell class="mr-2 size-4" />
              Будильники
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <template v-if="!telegramStore.isTelegramWebApp">
            <DropdownMenuSeparator />

            <DropdownMenuItem @select="showLogoutDialog = true">
              <LogOut class="mr-2 size-4" />
              Выйти
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>

  <AlertDialog v-model:open="showLogoutDialog">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Выйти из аккаунта?</AlertDialogTitle>
        <AlertDialogDescription> Для повторного входа потребуется авторизация через Telegram. </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Отмена</AlertDialogCancel>
        <AlertDialogAction @click="handleLogout">Выйти</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <SettingsDialog v-model:open="showSettingsDialog" />
  <MemoriesDialog v-model:open="showMemoriesDialog" />
  <AlarmsDialog v-model:open="showAlarmsDialog" />
</template>
